import { EXAMPLE_HOUSEHOLD, SPENDING_RULES, newPerson, newPot, type AssetMix, type Household, type SpendingRuleKind } from './household';
import { UK_HISTORY, replay } from './history';
import { mixReturn, mixVolatility } from './investments';
import { monteCarlo } from './montecarlo';
import { spendingRules } from './outlook';
import { project } from './project';
import { levelMix } from './risklevels';
import { DEFAULT_RULES } from './rules';

export interface RetirementLabInputs {
  age: number;
  horizonAge: number;
  pension: number;
  isa: number;
  annualIncome: number;
  essentialIncome: number;
  statePension: number;
  statePensionAge: number;
  riskLevel: number;
  charges: number;
  cashBucketYears: number;
  guardrails: boolean;
  runs: number;
}

export const DEFAULT_RETIREMENT_LAB_INPUTS: RetirementLabInputs = {
  age: 65,
  horizonAge: 95,
  pension: 350_000,
  isa: 100_000,
  annualIncome: 30_000,
  essentialIncome: 20_000,
  statePension: 12_550,
  statePensionAge: 67,
  riskLevel: 3,
  charges: 0.35,
  cashBucketYears: 2,
  guardrails: false,
  runs: 100,
};

export interface RetirementLabBaseResult {
  avgIncome: number;
  minIncome: number;
  maxIncome: number;
  endWealth: number;
  shortfallYears: number;
  path: { age: number; income: number }[];
}

export interface RetirementLabHistoryResult {
  starts: number;
  successRate: number;
  worstStart: number;
  worstMinIncome: number;
  medianMinIncome: number;
  medianEndWealth: number;
}

export interface RetirementLabRuleResult {
  kind: SpendingRuleKind;
  name: string;
  description: string;
  base: RetirementLabBaseResult;
  history: RetirementLabHistoryResult;
  simulation: {
    runs: number;
    successRate: number;
    endWealth: { p10: number; p50: number; p90: number };
  };
}

export interface RetirementLabResult {
  inputs: RetirementLabInputs;
  mix: AssetMix;
  expectedReturn: number;
  volatility: number;
  historyFrom: number;
  historyTo: number;
  rules: RetirementLabRuleResult[];
}

const finite = (value: number, fallback: number): number => Number.isFinite(value) ? value : fallback;
const clamp = (value: number, min: number, max: number): number => Math.max(min, Math.min(max, value));
const money = (value: number, fallback: number): number => Math.round(clamp(finite(value, fallback), 0, 100_000_000));

export function normaliseRetirementLabInputs(raw: Partial<RetirementLabInputs> = {}): RetirementLabInputs {
  const d = DEFAULT_RETIREMENT_LAB_INPUTS;
  const age = Math.round(clamp(finite(raw.age ?? d.age, d.age), 50, 85));
  const horizonAge = Math.round(clamp(finite(raw.horizonAge ?? d.horizonAge, d.horizonAge), age + 5, 105));
  const annualIncome = money(raw.annualIncome ?? d.annualIncome, d.annualIncome);
  return {
    age,
    horizonAge,
    pension: money(raw.pension ?? d.pension, d.pension),
    isa: money(raw.isa ?? d.isa, d.isa),
    annualIncome,
    essentialIncome: Math.min(annualIncome, money(raw.essentialIncome ?? d.essentialIncome, d.essentialIncome)),
    statePension: money(raw.statePension ?? d.statePension, d.statePension),
    statePensionAge: Math.round(clamp(finite(raw.statePensionAge ?? d.statePensionAge, d.statePensionAge), 60, 75)),
    riskLevel: Math.round(clamp(finite(raw.riskLevel ?? d.riskLevel, d.riskLevel), 1, 5)),
    charges: clamp(finite(raw.charges ?? d.charges, d.charges), 0, 3),
    cashBucketYears: Math.round(clamp(finite(raw.cashBucketYears ?? d.cashBucketYears, d.cashBucketYears), 0, 5)),
    guardrails: raw.guardrails ?? d.guardrails,
    runs: Math.round(clamp(finite(raw.runs ?? d.runs, d.runs), 50, 300)),
  };
}

function householdFor(inputs: RetirementLabInputs, mix: AssetMix, expectedReturn: number): Household {
  const pension = { ...newPot(inputs.pension, expectedReturn, 'retirement-lab-pot', 'Pension'), charges: inputs.charges, mix: { ...mix } };
  const person = newPerson({
    id: 'retirement-lab-person',
    name: 'You',
    age: inputs.age,
    retireAge: inputs.age,
    deathAge: inputs.horizonAge,
    pots: [pension],
    isa: inputs.isa,
    statePension: { amount: inputs.statePension, spa: inputs.statePensionAge, deferYears: 0, qualifyingYears: null },
  });
  return {
    ...EXAMPLE_HOUSEHOLD,
    people: [person],
    home: 0,
    homeMort: 0,
    homeMortRate: 0,
    homeMortYears: 0,
    homeMortIO: false,
    homeFix: null,
    homeDesc: false,
    otherProp: 0,
    otherMort: 0,
    otherCost: 0,
    otherRent: 0,
    otherMortRate: 0,
    otherSellAge: null,
    cash: 0,
    other: 0,
    business: 0,
    debts: 0,
    spending: 0,
    essential: inputs.essentialIncome,
    incomeTarget: inputs.annualIncome,
    targetSteps: [],
    withdrawalOrder: 'auto',
    infl: 2.5,
    payRises: true,
    todays: true,
    uprate: true,
    gifts7: 0,
    priorGifts: [],
    plan: { ...EXAMPLE_HOUSEHOLD.plan, giftIncome: 0, lump: 0, tfc: false, spend: 0, fdGift: 0, fdPen: 0 },
    lens: 'retirement',
    useIsaAllowance: false,
    potGoal: null,
    events: [],
    monteCarlo: { enabled: true, volatility: null, runs: inputs.runs, method: 'history' },
    isaMix: null,
    gia: 0,
    giaCost: 0,
    giaMix: null,
    useMarket: false,
    rebalance: true,
    bucketYears: inputs.cashBucketYears,
    guardrails: { enabled: inputs.guardrails, cut: 10, band: 20 },
    spendingRule: { kind: 'constant', rate: 4 },
    care: null,
    gInv: expectedReturn - inputs.charges,
  };
}

function median(values: number[]): number {
  if (!values.length) return 0;
  const ordered = values.slice().sort((a, b) => a - b);
  const middle = Math.floor(ordered.length / 2);
  return ordered.length % 2 ? ordered[middle]! : (ordered[middle - 1]! + ordered[middle]!) / 2;
}

function resultOf(h: Household) {
  const r = project(h, h.people[0]!.deathAge - h.people[0]!.age, DEFAULT_RULES);
  const retirementRows = r.rows.filter(row => row.target !== null);
  const delivered = retirementRows.map(row => Math.max(0, (row.target ?? 0) - row.shortfall) / row.f);
  const last = r.rows[r.rows.length - 1];
  return {
    result: r,
    minIncome: delivered.length ? Math.min(...delivered) : 0,
    maxIncome: delivered.length ? Math.max(...delivered) : 0,
    avgIncome: delivered.length ? delivered.reduce((sum, value) => sum + value, 0) / delivered.length : 0,
    endWealth: last ? (last.pots + last.cash + last.isa + last.gia) / last.f : 0,
    path: retirementRows.map((row, index) => ({ age: row.age, income: delivered[index]! })),
  };
}

/**
 * Compare four common retirement withdrawal approaches on one tax-aware household.
 * Historical starts use every complete UK data window; random futures resample blocks of those same years.
 */
export function retirementIncomeLab(raw: Partial<RetirementLabInputs> = {}): RetirementLabResult {
  const inputs = normaliseRetirementLabInputs(raw);
  const mix = levelMix(inputs.riskLevel, DEFAULT_RULES);
  const grossReturn = mixReturn(mix, DEFAULT_RULES);
  const household = householdFor(inputs, mix, grossReturn);
  const metadata = new Map(spendingRules(household, DEFAULT_RULES).map(rule => [rule.kind, rule]));
  const years = inputs.horizonAge - inputs.age;
  const lastStart = UK_HISTORY.to - years + 1;

  const ruleResults = SPENDING_RULES.map(kind => {
    const h: Household = { ...household, spendingRule: { ...household.spendingRule, kind } };
    const base = resultOf(h);
    const historical: { start: number; minIncome: number; endWealth: number; success: boolean }[] = [];
    for (let start = UK_HISTORY.from; start <= lastStart; start++) {
      const scenario = replay(h, start, 1, DEFAULT_RULES, mix);
      if (scenario.covered < years) continue;
      const projection = project(h, years, DEFAULT_RULES, { yearlyDelta: scenario.yearlyDelta, yearlyInflation: scenario.yearlyInflation });
      const rows = projection.rows.filter(row => row.target !== null);
      const delivered = rows.map(row => Math.max(0, (row.target ?? 0) - row.shortfall) / row.f);
      const last = projection.rows[projection.rows.length - 1];
      historical.push({
        start,
        minIncome: delivered.length ? Math.min(...delivered) : 0,
        endWealth: last ? (last.pots + last.cash + last.isa + last.gia) / last.f : 0,
        success: projection.shortfallYears === 0,
      });
    }
    const worst = historical.slice().sort((a, b) => a.minIncome - b.minIncome || a.endWealth - b.endWealth)[0];
    const mc = monteCarlo(h, DEFAULT_RULES, 12345, mix);
    const end = mc.wealth[mc.wealth.length - 1] ?? { p10: 0, p50: 0, p90: 0 };
    const meta = metadata.get(kind)!;
    return {
      kind,
      name: kind === 'percent' ? `${meta.name} (4%)` : meta.name,
      description: meta.description,
      base: {
        avgIncome: base.avgIncome,
        minIncome: base.minIncome,
        maxIncome: base.maxIncome,
        endWealth: base.endWealth,
        shortfallYears: base.result.shortfallYears,
        path: base.path,
      },
      history: {
        starts: historical.length,
        successRate: historical.length ? historical.filter(row => row.success).length / historical.length : 0,
        worstStart: worst?.start ?? UK_HISTORY.from,
        worstMinIncome: worst?.minIncome ?? 0,
        medianMinIncome: median(historical.map(row => row.minIncome)),
        medianEndWealth: median(historical.map(row => row.endWealth)),
      },
      simulation: {
        runs: mc.runs,
        successRate: mc.successRate,
        endWealth: { p10: end.p10, p50: end.p50, p90: end.p90 },
      },
    } satisfies RetirementLabRuleResult;
  });

  return {
    inputs,
    mix,
    expectedReturn: grossReturn - inputs.charges,
    volatility: mixVolatility(mix, DEFAULT_RULES),
    historyFrom: UK_HISTORY.from,
    historyTo: UK_HISTORY.to,
    rules: ruleResults,
  };
}
